import { Fragment, type KeyboardEvent, type ReactNode } from 'react';
import { fmtDayMonth } from '../../lib/format';
import { betragText, farbe, hatHart, ibanText, STATUS_TEXT, textSpalte } from './logik';
import type { BankkontoSicht, RechnungsZeile, ZeileSicht } from './types';

// Tabelle der Bankzeilen in Sams Reihenfolge: Datum · Beleg-Nr. · St-Sl · Gegenkonto · Konto · Betrag · Buchungstext.
// Links ein Farbstreifen (grün bestätigt, gelb Vorschlag, rot offen oder harter Befund, grau verworfen).
// Teilbuchungen stehen eingerückt unter der Bankzeile. Die gewählte Zeile hat den Fokus (↑↓, Enter, Ziffern).
// inline: Detail direkt unter der gewählten Zeile (schmale Fenster und Handy).

export const SPALTEN = ['Datum', 'Beleg-Nr.', 'St-Sl', 'Gegenkonto', 'Konto', 'Betrag', 'Buchungstext'] as const;

function Kopf() {
  return (
    <thead>
      <tr>
        {SPALTEN.map((s) => (
          <th key={s} className={s === 'Betrag' ? 'r' : undefined} scope="col">
            {s}
          </th>
        ))}
      </tr>
    </thead>
  );
}

const Betrag = ({ cents }: { cents: number }) => (
  <span className={`num ${cents < 0 ? 'neg' : 'pos'}`}>{betragText(cents, { plus: true })}</span>
);

export function BuchenTabelle({
  zeilen,
  konten,
  auswahl,
  geaendert,
  inline,
  detail,
  onWaehle,
  onTaste,
}: {
  zeilen: ZeileSicht[];
  konten: BankkontoSicht[];
  auswahl: string | null;
  // Zeilen mit ungespeicherter Änderung
  geaendert: Set<string>;
  inline: boolean;
  detail: ReactNode;
  onWaehle(id: string): void;
  onTaste(e: KeyboardEvent<HTMLTableRowElement>, z: ZeileSicht): void;
}) {
  const mehrereKonten = new Set(zeilen.map((z) => z.bankkontoId)).size > 1;
  let letztesKonto: string | null = null;
  return (
    <div className="bt-w">
      <table className="bt" role="grid" aria-label="Bankzeilen" aria-readonly="true">
        <Kopf />
        <tbody>
          {zeilen.map((z, idx) => {
            const kopfKonto = mehrereKonten && z.bankkontoId !== letztesKonto;
            letztesKonto = z.bankkontoId;
            const k = konten.find((x) => x.id === z.bankkontoId);
            const sel = z.id === auswahl;
            const einTeil = z.teile.length === 1 ? z.teile[0]! : null;
            const geteilt = z.teile.length > 1;
            return (
              <Fragment key={z.id}>
                {kopfKonto && (
                  <tr className="bt-konto">
                    <td colSpan={7}>
                      {k ? `${k.bezeichnung} ${k.finanzkonto} · ${ibanText(k.iban)}` : z.konto}
                    </td>
                  </tr>
                )}
                <tr
                  className={`bt-z f-${farbe(z)}${sel ? ' sel' : ''}${geteilt ? ' hat-teile' : ''}`}
                  data-zeile={z.id}
                  // eine Zeile ist immer per Tab erreichbar (die gewählte, sonst die erste)
                  tabIndex={sel || (idx === 0 && !zeilen.some((y) => y.id === auswahl)) ? 0 : -1}
                  aria-selected={sel}
                  onClick={() => onWaehle(z.id)}
                  onKeyDown={(e) => onTaste(e, z)}
                >
                  <td className="c-dat" data-l="Datum">
                    <span className="sr-only">{STATUS_TEXT[z.status]}, </span>
                    <span className="num">{fmtDayMonth(z.buchungstag)}</span>
                  </td>
                  <td className="c-bnr" data-l="Beleg-Nr.">
                    {z.belegfeld1 && <span className="num">{z.belegfeld1}</span>}
                  </td>
                  <td className="c-st" data-l="St-Sl">
                    {einTeil?.bu && (
                      <span className="num" title={einTeil.buText ?? undefined}>
                        {einTeil.bu}
                      </span>
                    )}
                  </td>
                  <td className="c-gk" data-l="Gegenkonto">
                    {einTeil ? (
                      <>
                        <span className="num">{einTeil.gegenkonto}</span>
                        {einTeil.gegenkontoName && <span className="sub">{einTeil.gegenkontoName}</span>}
                      </>
                    ) : geteilt ? (
                      <span className="muted">aufgeteilt</span>
                    ) : z.status === 'verworfen' ? null : (
                      <span className="bt-fehlt">fehlt</span>
                    )}
                  </td>
                  <td className="c-kto" data-l="Konto">
                    <span className="num">{z.konto}</span>
                  </td>
                  <td className="c-btr r">
                    <Betrag cents={z.betragCents} />
                  </td>
                  <td className="c-txt">
                    <div className="bt-tx">
                    <span className="bt-text">{textSpalte(z)}</span>
                    <span className="bt-marks">
                      {z.sicher && <span className="pill p-ok">sicher</span>}
                      {z.quelleVorschlag === 'ki' && <span className="pill m-ki">KI</span>}
                      {z.befunde.length > 0 && (
                        <span className={`pill ${hatHart(z) ? 'p-bad' : 'p-warn'}`} title={z.befunde.map((b) => b.satz).join(' ')}>
                          !
                        </span>
                      )}
                      {z.quelle === 'hand' ? <span className="pill p-info">von Hand</span> : !z.imAuszug && <span className="pill p-bad">nicht mehr im Auszug</span>}
                      {z.status === 'verworfen' && <span className="pill p-neu">verworfen</span>}
                      {geaendert.has(z.id) && <span className="pill p-info">geändert</span>}
                    </span>
                    </div>
                  </td>
                </tr>
                {geteilt &&
                  z.teile.map((t) => (
                    <tr key={t.id} className={`bt-teil f-${farbe(z)}${sel ? ' sel' : ''}`} onClick={() => onWaehle(z.id)}>
                      <td className="c-dat" />
                      <td className="c-bnr" data-l="Beleg-Nr.">
                        {t.belegfeld1 && t.belegfeld1 !== z.belegfeld1 && <span className="num">{t.belegfeld1}</span>}
                      </td>
                      <td className="c-st" data-l="St-Sl">
                        {t.bu && (
                          <span className="num" title={t.buText ?? undefined}>
                            {t.bu}
                          </span>
                        )}
                      </td>
                      <td className="c-gk">
                        <span className="num">{t.gegenkonto}</span>
                        {t.gegenkontoName && <span className="sub">{t.gegenkontoName}</span>}
                      </td>
                      <td className="c-kto" />
                      <td className="c-btr r">
                        <Betrag cents={t.betragCents} />
                      </td>
                      <td className="c-txt">
                        {z.lohn === 'nein' && t.buchungstext && (
                          <div className="bt-tx">
                            <span className="bt-text">{t.buchungstext}</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                {sel && inline && (
                  <tr className="bt-det">
                    <td colSpan={7}>{detail}</td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// „Rechnungen aus dem Rechnungsprogramm“: Debitor an Erlös, nur Anzeige (fließt in den Export)
export function RechnungenTabelle({ rechnungen, onBeleg }: { rechnungen: RechnungsZeile[]; onBeleg(id: string): void }) {
  return (
    <div className="bt-w">
      <table className="bt bt-re" aria-label="Rechnungen aus dem Rechnungsprogramm">
        <Kopf />
        <tbody>
          {rechnungen.map((r, i) => (
            <tr key={`${r.rtRechnungId}-${i}`} className="bt-z f-re">
              <td className="c-dat" data-l="Datum">
                <span className="num">{fmtDayMonth(r.datum)}</span>
              </td>
              <td className="c-bnr" data-l="Beleg-Nr.">
                {r.belegId ? (
                  <button type="button" className="linkbtn num" onClick={() => onBeleg(r.belegId!)} title="Beleg ansehen">
                    {r.belegfeld1}
                  </button>
                ) : (
                  <span className="num">{r.belegfeld1}</span>
                )}
              </td>
              <td className="c-st" data-l="St-Sl">
                {r.bu && <span className="num">{r.bu}</span>}
              </td>
              <td className="c-gk" data-l="Gegenkonto">
                <span className="num">{r.gegenkonto}</span>
                {r.gegenkontoName && <span className="sub">{r.gegenkontoName}</span>}
              </td>
              <td className="c-kto" data-l="Konto">
                <span className="num">{r.konto}</span>
              </td>
              <td className="c-btr r">
                <Betrag cents={r.betragCents} />
              </td>
              <td className="c-txt">
                <div className="bt-tx">
                  <span className="bt-text">{r.buchungstext}</span>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
